// ===== API contracts (match mezzo-service VoxRoi DTOs) =====

export interface VoxRoiConfigResponse {
  encryptedData: string;
  version: number;
  modifiedDate: string;
}

export interface VoxRoiConfigRequest {
  encryptedData: string;
  version: number;
}

export interface VoxProductMapping {
  reportType: string;
  dataType: string | null;
  smartOutreach: boolean | null;
}

export interface VoxProductCatalogItem {
  vendor: string;
  productKey: string;
  productDisplayName: string;
  mappings: VoxProductMapping[];
  sortOrder: number;
}

export interface VoxRoiProductUsageItem {
  date: string;
  productKey: string;
  isMapped: boolean;
  vendor: string;
  transactionName: string;
  reportType: string;
  dataType: string;
  isSmartOutreach: boolean;
  successCount: number;
}

export interface VoxRoiDailyUnitCount {
  date: string;
  unitCount: number;
}

export interface VoxRoiUsageResponse {
  totalUnitCount: number;
  unitCounts: VoxRoiDailyUnitCount[];
  productUsage: VoxRoiProductUsageItem[];
}

// ===== Decrypted config (never sent to Mezzo in plaintext) =====

export const VOX_ROI_CONFIG_VERSION = 3;

export interface CostScheduleEntry {
  effectiveFrom: string;
  effectiveTo: string | null;
  /** null means the cost has not been entered yet (missing), which is distinct from $0. */
  cost: number | null;
}

export interface BeforeProviderEntry {
  ordersPerLoan: number;
  cost: number;
}

export interface VoxRoiCostConfigData {
  version: number;
  /** date-effective cost schedules per product, keyed by catalog productKey */
  costs: Record<string, CostScheduleEntry[]>;
  /** per-provider before config keyed by catalog productKey */
  beforeProviders: Record<string, BeforeProviderEntry>;
}

// ===== Client-side ROI result =====

export interface VoxRoiBeforeProvider {
  productKey: string;
  vendor: string;
  productDisplayName: string;
  ordersPerLoan: number;
  costPerOrder: number | undefined;
}

/**
 * Whether a product/group's successful orders are fully priced, partly priced, or entirely
 * unpriced. `missing` and `partial` mean the known spend is only a subtotal, so any total that
 * includes this line is incomplete. Missing prices are never coerced to a known $0.00.
 */
export type PricingState = 'complete' | 'partial' | 'missing';

export interface VoxRoiAfterLine {
  productKey: string;
  vendor: string;
  productDisplayName: string;
  /** catalog sortOrder for stable provider/line ordering */
  sortOrder: number;
  /** total successful orders for this product in the window */
  successCount: number;
  /** orders that had an effective cost on their date */
  pricedCount: number;
  /** orders with no effective cost configured (missing price, not $0) */
  unpricedCount: number;
  /** sum over priced orders of (effective cost x count); excludes unpriced orders */
  knownSpend: number;
  pricingState: PricingState;
}

export interface CostGapWarning {
  productKey: string;
  vendor: string;
  productDisplayName: string;
  /** number of successful orders left unpriced because no cost was configured */
  affectedOrderCount: number;
  dates: string[];
}

/** Unmapped usage collapsed to a stable service-provided mapping signature (no costs, no PII). */
export interface UnmappedUsageGroup {
  signature: string;
  vendor: string;
  transactionName: string;
  reportType: string;
  dataType: string;
  isSmartOutreach: boolean;
  /** summed SuccessCount across the rows sharing this signature */
  successCount: number;
}

export interface VoxRoiResult {
  totalUnitCount: number;
  before: {
    costPerLoan: number;
    totalSpend: number;
    providerCount: number;
    providers: VoxRoiBeforeProvider[];
  };
  after: {
    /** known (priced) After spend only; a lower bound when pricingState is not complete */
    knownSpend: number;
    avgCostPerLoan: number;
    lines: VoxRoiAfterLine[];
    pricingState: PricingState;
    /** total successful orders with no configured cost across all mapped products */
    unpricedOrderCount: number;
    /** successful orders that had an effective cost on their date */
    pricedOrderCount: number;
    /** true when there is After activity but no priced orders (including all-unmapped) */
    afterFullyUnpriced: boolean;
  };
  savings: {
    /** null when afterFullyUnpriced — savings cannot be computed */
    total: number | null;
    pct: number | null;
    /** null when afterFullyUnpriced */
    perLoan: number | null;
  };
  warnings: {
    unmappedUsage: UnmappedUsageGroup[];
    /** summed SuccessCount across all unmapped groups, for count reconciliation */
    unmappedOrderCount: number;
    productsMissingCost: CostGapWarning[];
    /** true when any missing price or unmapped activity makes totals incomplete */
    hasIncompletePricing: boolean;
    /** mirrors after.afterFullyUnpriced for warning-panel rendering */
    afterFullyUnpriced: boolean;
  };
}

/** Which cost-config section is currently in edit mode (null = all read-only). */
export type VoxRoiEditSection = 'lenderInputs' | 'costs' | null;
